import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Tag,
  Space,
  Select,
  Switch,
  Popconfirm,
  Typography,
  Tooltip,
} from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import useDebounce from "../hooks/useDebounce";
import apiPath from "../constants/apiPath";
import lang from "../helper/lang";
import { Trash2 } from "lucide-react";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

function CouponManager() {
  const { request } = useRequest();
  const requestRef = useRef(request);

  const [coupons, setCoupons] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [couponToDelete, setCouponToDelete] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 500);
  const [deleteLoading, setDeleteLoading] = useState({});
  const [editingCoupon, setEditingCoupon] = useState(null);

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  // ===============================
  // Fetch Coupons
  // ===============================
  const fetchCoupons = useCallback(() => {
    setLoading(true);

    const params = new URLSearchParams();
    params.append("page", pagination.current);
    params.append("limit", pagination.pageSize);

    if (debouncedSearchText) params.append("search", debouncedSearchText);

    requestRef.current({
      url: `/api/admin/coupons?${params.toString()}`,
      method: "GET",
      onSuccess: (res) => {
        setLoading(false);
        setCoupons(res?.data || []);
        setPagination((prev) => ({
          ...prev,
          current: Number(res?.pagination?.page) || prev.current,
          pageSize: Number(res?.pagination?.limit) || prev.pageSize,
          total: res?.pagination?.total || 0, // 👈 backend total count
        }));
      },
      onError: () => {
        setLoading(false);
        ShowToast("Failed to fetch coupons", Severty.ERROR);
      },
    });
  }, [debouncedSearchText, pagination.current, pagination.pageSize]);

  const handleTableChange = (paginationInfo) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const resetDeleteModal = () => {
    setDeleteModalVisible(false);
    setCouponToDelete(null);
  };

  // ===============================
  // Fetch Subscription Packages
  // ===============================
  const fetchPackages = useCallback(() => {
    requestRef.current({
      url: `/api/${apiPath.subscriptions}`,
      method: "GET",
      onSuccess: (res) => {
        setPackages(res?.data || []);
      },
    });
  }, []);

  useEffect(() => {
    fetchCoupons();
    fetchPackages();
  }, [fetchCoupons, fetchPackages]);

  // ===============================
  // Handlers
  // ===============================
  const handleAddCoupon = () => {
    setEditingCoupon(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditCoupon = (coupon) => {
    setEditingCoupon(coupon);

    form.setFieldsValue({
      code: coupon.code,
      description: coupon.description,
      package_id: coupon.package_id,
      usage_limit: coupon.usage_limit,
      is_active: coupon.is_active,
    });

    setModalVisible(true);
  };

  const handleToggleStatus = (coupon) => {
    requestRef.current({
      url: `/api/admin/coupons/${coupon.id}`,
      method: "PUT",
      data: {
        is_active: !coupon.is_active,
      },
      onSuccess: () => {
        ShowToast(
          `Coupon ${coupon.is_active ? "deactivated" : "activated"} successfully`,
          Severty.SUCCESS,
        );
        fetchCoupons();
      },
      onError: () => {
        ShowToast("Failed to update coupon status", Severty.ERROR);
      },
    });
  };

  const handleDeleteCoupon = (coupon) => {
    requestRef.current({
      url: `/api/admin/coupons/${coupon.id}`,
      method: "DELETE",
      onSuccess: () => {
        ShowToast("Coupon deleted successfully", Severty.SUCCESS);
        fetchCoupons();
      },
      onError: () => {
        ShowToast("Failed to delete coupon", Severty.ERROR);
      },
    });
  };

  const handleFormSubmit = (values) => {
    const isEdit = !!editingCoupon;

    requestRef.current({
      url: isEdit
      ? `/api/admin/coupons/${editingCoupon.id}`
      : `/api/admin/coupons`,
      method: isEdit ? "PUT" : "POST",
      data: {
        code: values.code,
        description: values.description || "",
        package_id: values.package_id || null,
        usage_limit: values.usage_limit || null,
        is_active: values.is_active,
      },
      onSuccess: () => {
        ShowToast(
          isEdit ? "Coupon updated successfully" : "Coupon created successfully",
          Severty.SUCCESS
        );
        setModalVisible(false);
        setEditingCoupon(null);
        form.resetFields();
        fetchCoupons();
      },
      onError: (err) => {
        ShowToast(
          err?.response?.data?.message ||
            (isEdit ? "Failed to update coupon" : "Failed to create coupon"),
          Severty.ERROR
        );
      },
    });
  };

  // ===============================
  // Table Columns
  // ===============================
  const columns = [
    {
      title: "S. No.",
      key: "serial_no",
      width: 80,
      render: (_, __, index) => {
        const serialNumber =
          (pagination.current - 1) * pagination.pageSize + index + 1;
        return <Text>{serialNumber}</Text>;
      },
    },
    // {
    //   title: "ID",
    //   dataIndex: "id",
    //   width: 80,
    // },
    {
      title: "Coupon Code",
      dataIndex: "code",
      render: (code) => <strong>{code}</strong>,
    },
    {
      title: "Package",
      dataIndex: "package_name",
      render: (pkg) => pkg || "All Packages",
    },
    {
      title: "Usage",
      render: (_, r) =>
        r.usage_limit ? `${r.used_count}/${r.usage_limit}` : "Unlimited",
    },
    {
      title: "Status",
      dataIndex: "is_active",
      render: (active) => (
        <Tag color={active ? "green" : "red"}>
          {active ? "ACTIVE" : "INACTIVE"}
        </Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: "Actions",
      width: 120,
      render: (_, record) => (
        <Space>
          {/* Activate / Deactivate */}
          <Switch
            style={{ width: 70 }}
            checked={record.is_active}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
            onChange={() => handleToggleStatus(record)}
          />

          {/* Edit */}
          <Tooltip title="Edit">
            <Button
              className="button-action-small"
              icon={<EditOutlined />}
              style={{ color: "#ff4d4f" }}
              // size="small"
              onClick={() => handleEditCoupon(record)}
            />
          </Tooltip>

          {/* Delete */}
          {/* <Popconfirm
            title="Delete coupon?"
            onConfirm={() => handleDeleteCoupon(record)}
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm> */}

          <Tooltip title={lang("Delete") || "Delete"}>
            <Button
              className="button-action-small"
              icon={<Trash2 />}
              style={{ color: "#ff4d4f" }}
              // loading={deleteLoading[record._id || record.id]}
              // disabled={
              //   statusToggleLoading[record._id || record.id] ||
              //   deleteLoading[record._id || record.id]
              // }
              onClick={() => {
                setCouponToDelete(record);
                setDeleteModalVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title={
          <>
            <span>Coupon Manager</span>
            <p>Manage coupons for subscription packages</p>
          </>
        }
        extra={
          <Space>
            <Search
              placeholder="Search coupon"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
              }}
              onSearch={handleSearch}
              style={{ width: 200 }}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCoupon}
            >
              Add Coupon
            </Button>
          </Space>
        }
      >
        <div className="table-responsive">
          <Table
            columns={columns}
            dataSource={coupons}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
            }}
            onChange={handleTableChange}
          />
        </div>
      </Card>

      {/* ===============================
          Create Coupon Modal
      =============================== */}

      {/* Delete User Modal */}
      <DeleteConfirmationModal
        visible={deleteModalVisible}
        key={couponToDelete?.id || couponToDelete?._id || "delete-modal"}
        onCancel={resetDeleteModal}
        onConfirm={(reason) => {
          handleDeleteCoupon(couponToDelete);
          resetDeleteModal();
        }}
        title={"Delete Coupon"}
        message={"Are you sure you want to delete this coupon ?"}
        itemName={couponToDelete?.name || couponToDelete?.fullName}
        loading={
          couponToDelete &&
          deleteLoading[couponToDelete._id || couponToDelete.id]
        }
        okText={lang("Delete") || "Delete"}
        cancelText={lang("Cancel") || "Cancel"}
        input={false}
      />

      <Modal
        title={editingCoupon ? "Edit Coupon" : "Create Coupon"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          <Form.Item
            name="code"
            label="Coupon Code"
            rules={[{ required: true, message: "Enter coupon code" }]}
          >
            <Input disabled={!!editingCoupon} placeholder="e.g. NEWYEAR50" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item name="package_id" label="Subscription Package">
            <Select placeholder="Select package" allowClear>
              {packages.map((pkg) => (
                <Option key={pkg.id} value={pkg.id}>
                  {pkg.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="usage_limit" label="Usage Limit">
            <Input type="number" min={1} />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item style={{ textAlign: "right" }}>
            <Button
              onClick={() => setModalVisible(false)}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              {editingCoupon ? "Update" : "Create"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default CouponManager;
